import {
  AI_OPERATIONS_MANAGER_CLASSIFICATION_LABEL,
  getSolutionTeamDefaultFeaturePolicies,
  isAiOperationsManagerSolutionKey,
} from './aiOperationsManagerConfig';
import {
  getAiProposalManagerDefaultFeaturePolicies,
  isAiProposalManagerSolutionKey,
} from './aiProposalManagerConfig';
import {
  expandWithSmartTaskCompanions,
  type IndustrySolutionConfigRegistry,
} from '@/config/industry-solutions/configLoader';
import type { OperationalWorkflowTemplateConfig } from '@/config/industry-solutions/types';
import {
  normalizeOperationsActionPermissions,
  OPERATIONS_ACTION_KEYS,
  type OperationsActionMap,
  type OperationsActionPermissions,
} from './actionPermissions';
import { getSolutionPolicyDefaults } from './solutionPolicyDefaults';

export const SPECIALISED_AI_SOLUTION_CLASSIFICATION_LABEL = AI_OPERATIONS_MANAGER_CLASSIFICATION_LABEL;

export function isSpecialisedAiSolutionKey(solutionKey?: string | null): boolean {
  return isAiOperationsManagerSolutionKey(solutionKey) || isAiProposalManagerSolutionKey(solutionKey);
}

export function isStandardOperationsSolutionKey(solutionKey?: string | null): solutionKey is string {
  return typeof solutionKey === 'string'
    && solutionKey.trim().length > 0
    && !isSpecialisedAiSolutionKey(solutionKey);
}

/**
 * A `requires_permission` value in the `row_key.action_key` dotted format
 * (e.g. `my_vehicle.smart_task`, `job.create`) is a coordinate into the
 * operations action matrix — not a standalone feature permission key. These
 * must not be collected into `allowed_permission_keys`/feature policies; they
 * are governed by the Actions tab matrix.
 */
function isMatrixPermissionCoordinate(permissionKey: string): boolean {
  const dotIndex = permissionKey.indexOf('.');
  if (dotIndex <= 0) return false;
  const actionKey = permissionKey.slice(dotIndex + 1);
  return (OPERATIONS_ACTION_KEYS as readonly string[]).includes(actionKey);
}

export function getDefaultSolutionTeamFeaturePoliciesForSolution(solutionKey?: string | null) {
  if (isAiProposalManagerSolutionKey(solutionKey)) {
    return getAiProposalManagerDefaultFeaturePolicies(solutionKey);
  }

  return getSolutionTeamDefaultFeaturePolicies(solutionKey);
}

/**
 * Map a solution key to the RBAC permission keys for its workflow modals.
 *
 * Each industry solution has a set of workflow templates (from mappings.yaml).
 * Each workflow template may have a corresponding modal in smarttask-config.yaml
 * with an `rbac_permission` key. This function looks up the solution's
 * workflow templates, finds their modals, and returns the rbac_permission keys.
 *
 * These keys are seeded into permission_group_policies when the solution is
 * activated, granting group members access to the workflow modals.
 *
 * @param solutionKey - The industry solution key (e.g. 'security-services')
 * @param registry - The config registry (from getConfigRegistry())
 * @param enabledTemplateKeys - Optional set of template keys that are enabled.
 *   When provided, only permissions for these templates are returned. When
 *   omitted, all templates from the solution's mappings are included.
 * @returns Array of rbac_permission keys (e.g. ['security_services_access', 'fleet_management_access'])
 */
export function getWorkflowModalPermissionsForSolution(
  solutionKey: string,
  registry: IndustrySolutionConfigRegistry,
  enabledTemplateKeys?: Set<string> | null,
): string[] {
  const solution = registry.getSolution(solutionKey);
  if (!solution?.mappings?.workflow_templates) {
    return [];
  }

  const smartTaskConfig = registry.getSmartTaskConfig();
  if (!smartTaskConfig) {
    return [];
  }

  const permissionKeys = new Set<string>();
  for (const templateKey of expandWithSmartTaskCompanions(solution.mappings.workflow_templates)) {
    // Filter to enabled templates when a filter set is provided
    if (enabledTemplateKeys && !enabledTemplateKeys.has(templateKey)) continue;
    // getWorkflowModals merges the workflow YAML's workflow_actions into each
    // modal's action list — seeded permissions must reflect the merged view.
    for (const modal of registry.getWorkflowModals(templateKey)) {
      const rbacPermission = modal.config?.rbac_permission;
      if (rbacPermission) {
        permissionKeys.add(rbacPermission);
      }
    }
  }
  return Array.from(permissionKeys);
}

/**
 * Map a solution key to ALL permission keys applicable to it.
 *
 * This includes:
 *   1. Workflow modal `rbac_permission` keys (modal-level access)
 *   2. Action-level `requires_permission` keys (action-level access within modals)
 *   3. Default solution feature policy keys (message_centre, job_threads, etc.)
 *   4. The `operations_action_permissions` key (for the action permissions table)
 *
 * This is the authoritative list of permissions that may be toggled for a
 * solution-sourced permission group. The GroupsSection editor uses this to
 * filter which permissions are shown — preventing an Advanced Manufacturing
 * group from showing Fleet Management permissions, etc.
 *
 * @param solutionKey - The industry solution key (e.g. 'security-services')
 * @param registry - The config registry (from getConfigRegistry())
 * @param enabledTemplateKeys - Optional set of template keys that are enabled.
 *   When provided, only permissions for these templates are returned. When
 *   omitted, all templates from the solution's mappings are included.
 * @returns Array of permission keys that are valid for this solution
 */
export function getSolutionPermissionKeys(
  solutionKey: string,
  registry: IndustrySolutionConfigRegistry,
  enabledTemplateKeys?: Set<string> | null,
): string[] {
  const keys = new Set<string>();

  // 1 & 2: Workflow modal + action permissions
  const solution = registry.getSolution(solutionKey);
  if (solution?.mappings?.workflow_templates) {
    const smartTaskConfig = registry.getSmartTaskConfig();
    if (smartTaskConfig) {
      for (const templateKey of expandWithSmartTaskCompanions(solution.mappings.workflow_templates)) {
        // Filter to enabled templates when a filter set is provided
        if (enabledTemplateKeys && !enabledTemplateKeys.has(templateKey)) continue;

        // Derived feature keys are authoritative (modal rbac_permission +
        // configurable/non-shared setting keys — see getWorkflowFeatureDefaults).
        const derivedFeatures = getWorkflowFeatureDefaults(templateKey, registry);
        if (derivedFeatures) {
          for (const k of Object.keys(derivedFeatures)) keys.add(k);
          continue;
        }

        for (const modal of registry.getWorkflowModals(templateKey)) {
          // Action-level permissions — skip dotted matrix coordinates
          // (`row.action`); those live in the Actions tab matrix.
          if (modal.config?.actions) {
            for (const action of modal.config.actions) {
              if (action.requires_permission && !isMatrixPermissionCoordinate(action.requires_permission)) {
                keys.add(action.requires_permission);
              }
            }
          }
        }
      }
    }

    // 2b: Configurable and shared setting keys from workflow template configs.
    // These are the permission keys that each workflow template YAML declares
    // as configurable (e.g. require_daily_pre_trip_inspection, enable_photo_capture)
    // or shared across workflows (e.g. smart_task_field_worker). Only collect
    // these for workflows that produced no derived feature keys.
    for (const templateKey of expandWithSmartTaskCompanions(solution.mappings.workflow_templates)) {
      if (enabledTemplateKeys && !enabledTemplateKeys.has(templateKey)) continue;
      if (getWorkflowFeatureDefaults(templateKey, registry)) continue;
      const template = registry.getWorkflowTemplate(templateKey);
      if (!template?.config?.smart_task) continue;
      const smartTask = template.config.smart_task as Record<string, unknown>;
      const configurableKeys = Array.isArray(smartTask.configurable_setting_keys)
        ? smartTask.configurable_setting_keys as string[]
        : [];
      const sharedKeys = Array.isArray(smartTask.shared_setting_keys)
        ? smartTask.shared_setting_keys as string[]
        : [];
      for (const k of [...configurableKeys, ...sharedKeys]) {
        if (typeof k === 'string') keys.add(k);
      }
    }
  }

  // Shared SmartTask keys (shared_setting_keys used by 2+ workflows globally)
  for (const k of getSharedSmartTaskSettingKeys(registry)) keys.add(k);

  // 3: Default solution feature policy keys (communication, my_tasks, etc.)
  const defaultPolicies = getDefaultSolutionTeamFeaturePoliciesForSolution(solutionKey);
  for (const key of Object.keys(defaultPolicies)) {
    keys.add(key);
  }

  // 4: operations_action_permissions (the JSONB key for per-item-type action toggles)
  keys.add('operations_action_permissions');

  return Array.from(keys);
}

/**
 * Build a mapping from permission key → workflow template key for a solution.
 *
 * This allows the GroupsSection UI to group permissions by their source workflow
 * (e.g. "Service Operations", "Fleet Management") instead of by generic category.
 *
 * Keys that are not tied to a specific workflow (default communication policies,
 * operations_action_permissions) are mapped to 'general'.
 *
 * @param solutionKey - The industry solution key
 * @param registry - The config registry
 * @param enabledTemplateKeys - Optional set of enabled template keys to filter by
 * @returns Map from permission key to workflow template key (or 'general')
 */
export function getWorkflowKeyMappingForSolution(
  solutionKey: string,
  registry: IndustrySolutionConfigRegistry,
  enabledTemplateKeys?: Set<string> | null,
): Record<string, string> {
  const mapping: Record<string, string> = {};

  const solution = registry.getSolution(solutionKey);
  if (solution?.mappings?.workflow_templates) {
    const smartTaskConfig = registry.getSmartTaskConfig();
    if (smartTaskConfig) {
      for (const templateKey of expandWithSmartTaskCompanions(solution.mappings.workflow_templates)) {
        if (enabledTemplateKeys && !enabledTemplateKeys.has(templateKey)) continue;

        // Derived feature keys are authoritative → this workflow.
        const derivedFeatures = getWorkflowFeatureDefaults(templateKey, registry);
        if (derivedFeatures) {
          for (const k of Object.keys(derivedFeatures)) {
            mapping[k] = templateKey;
          }
          continue;
        }

        for (const modal of registry.getWorkflowModals(templateKey)) {
          // Action-level permissions — skip dotted matrix coordinates
          // (`row.action`); those live in the Actions tab matrix.
          if (modal.config?.actions) {
            for (const action of modal.config.actions) {
              if (action.requires_permission && !isMatrixPermissionCoordinate(action.requires_permission)) {
                mapping[action.requires_permission] = templateKey;
              }
            }
          }
        }
      }
    }

    // Configurable and shared setting keys — skipped for workflows that
    // produced derived feature keys.
    for (const templateKey of expandWithSmartTaskCompanions(solution.mappings.workflow_templates)) {
      if (enabledTemplateKeys && !enabledTemplateKeys.has(templateKey)) continue;
      if (getWorkflowFeatureDefaults(templateKey, registry)) continue;
      const template = registry.getWorkflowTemplate(templateKey);
      if (!template?.config?.smart_task) continue;
      const smartTask = template.config.smart_task as Record<string, unknown>;
      const configurableKeys = Array.isArray(smartTask.configurable_setting_keys)
        ? smartTask.configurable_setting_keys as string[]
        : [];
      const sharedKeys = Array.isArray(smartTask.shared_setting_keys)
        ? smartTask.shared_setting_keys as string[]
        : [];
      for (const k of [...configurableKeys, ...sharedKeys]) {
        if (typeof k === 'string') mapping[k] = templateKey;
      }
    }
  }

  // Shared SmartTask keys (shared_setting_keys used by 2+ workflows) are
  // shell-level settings, not workflow-scoped — they belong to the solution
  // parent's 'general' set, administered in one place. Every workflow child
  // member is a parent member, so resolution is unchanged.
  for (const k of getSharedSmartTaskSettingKeys(registry)) {
    mapping[k] = 'general';
  }

  // Default solution policies and operations_action_permissions → 'general'
  const defaultPolicies = getDefaultSolutionTeamFeaturePoliciesForSolution(solutionKey);
  for (const key of Object.keys(defaultPolicies)) {
    if (!(key in mapping)) mapping[key] = 'general';
  }

  return mapping;
}

/**
 * Extract the list of item types from a workflow template's `applies_to_item_type`.
 * Handles both string (comma-separated) and string[] formats.
 */
export function getWorkflowItemTypes(template: OperationalWorkflowTemplateConfig): string[] {
  const appliesTo = template.applies_to_item_type;
  if (Array.isArray(appliesTo)) {
    return appliesTo.map((t) => t.trim().toLowerCase()).filter(Boolean);
  }
  if (typeof appliesTo === 'string') {
    return appliesTo.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  }
  return [];
}

/**
 * Extract SmartTask action keys from a workflow template's
 * `smart_task.begin_action.workflow_sections[*].actions`.
 *
 * When a workflow has SmartTask action definitions, these action keys become
 * the rows in the Actions tab matrix (instead of the generic item types from
 * `applies_to_item_type`). The `smart_task` column in the matrix controls
 * whether the corresponding button appears in the workflow's SmartTask modal
 * (via `WorkflowModalBase.tsx`).
 *
 * For workflows where the action keys match the item types (e.g.
 * service-operations: appointment, job, task, project), the behavior is
 * unchanged. For workflows where they differ (e.g. fleet-management:
 * my_vehicle, vehicle_inspection, capture_expense, change_vehicle), the
 * matrix now shows the actual button keys instead of irrelevant item types.
 */
export interface WorkflowSmartTaskAction {
  key: string;
  label: string;
  description?: string;
}

export function getWorkflowSmartTaskActions(template: OperationalWorkflowTemplateConfig): WorkflowSmartTaskAction[] {
  const smartTaskConfig = template.config?.smart_task;
  if (!smartTaskConfig || typeof smartTaskConfig !== 'object') return [];

  const beginAction = (smartTaskConfig as Record<string, unknown>).begin_action;
  if (!beginAction || typeof beginAction !== 'object') return [];

  const workflowSections = (beginAction as Record<string, unknown>).workflow_sections;
  if (!Array.isArray(workflowSections)) return [];

  const actions: WorkflowSmartTaskAction[] = [];
  const seenKeys = new Set<string>();

  for (const section of workflowSections) {
    if (!section || typeof section !== 'object') continue;
    const sectionActions = (section as Record<string, unknown>).actions;
    if (!Array.isArray(sectionActions)) continue;

    for (const action of sectionActions) {
      if (!action || typeof action !== 'object') continue;
      const key = (action as Record<string, unknown>).key;
      if (typeof key !== 'string' || !key.trim()) continue;

      const normalizedKey = key.trim().toLowerCase();
      if (seenKeys.has(normalizedKey)) continue;
      seenKeys.add(normalizedKey);

      const label = (action as Record<string, unknown>).label;
      const description = (action as Record<string, unknown>).description;

      actions.push({
        key: normalizedKey,
        label: typeof label === 'string' && label.trim() ? label.trim() : normalizedKey,
        description: typeof description === 'string' ? description.trim() : undefined,
      });
    }
  }

  return actions;
}

/**
 * Extract the `matrix_actions` list from a workflow template's `smart_task`
 * config. When present, these are the only action columns shown in the
 * Actions tab matrix for this workflow (instead of all 20+ columns).
 * When absent, the Actions tab falls back to showing all columns.
 */
export function getWorkflowMatrixActions(template: OperationalWorkflowTemplateConfig): string[] {
  const smartTaskConfig = template.config?.smart_task;
  if (!smartTaskConfig || typeof smartTaskConfig !== 'object') return [];

  const matrixActions = (smartTaskConfig as Record<string, unknown>).matrix_actions;
  if (!Array.isArray(matrixActions)) return [];

  return matrixActions
    .filter((a): a is string => typeof a === 'string' && a.trim().length > 0)
    .map((a) => a.trim());
}

/**
 * Extract the action keys from a workflow's SmartTask modal definitions in
 * `smarttask-config.yaml` (e.g. `my_vehicle`, `start_audit`, `quick_issue`).
 * These are the buttons rendered inside the workflow modal by
 * `WorkflowModalBase`, gated by the row's `smart_task` matrix cell — so each
 * must exist as a row in the Actions tab matrix.
 */
export function getWorkflowModalActionKeys(
  workflowKey: string,
  registry: IndustrySolutionConfigRegistry,
): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const modal of registry.getWorkflowModals(workflowKey)) {
    const actions = modal.config?.actions;
    if (!Array.isArray(actions)) continue;
    for (const action of actions) {
      const key = typeof action?.key === 'string' ? action.key.trim().toLowerCase() : '';
      if (key && !seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }
  return keys;
}

/**
 * `shared_setting_keys` that appear in the SmartTask config of 2+ operational
 * workflow templates. These belong to the 'smarttask' shared group rather
 * than any single workflow's feature list. Computed globally (across all
 * registered templates) and memoized per registry instance.
 */
const sharedSettingKeysCache = new WeakMap<IndustrySolutionConfigRegistry, Set<string>>();

function getSharedSmartTaskSettingKeys(registry: IndustrySolutionConfigRegistry): Set<string> {
  const cached = sharedSettingKeysCache.get(registry);
  if (cached) return cached;

  const keyCount: Record<string, number> = {};
  for (const template of registry.getRegistry().operational_workflows.values()) {
    const smartTask = template?.config?.smart_task as Record<string, unknown> | undefined;
    const keys = Array.isArray(smartTask?.shared_setting_keys)
      ? smartTask.shared_setting_keys as unknown[]
      : [];
    for (const key of keys) {
      if (typeof key === 'string' && key.trim()) {
        keyCount[key] = (keyCount[key] || 0) + 1;
      }
    }
  }

  const shared = new Set(Object.keys(keyCount).filter((k) => keyCount[k] >= 2));
  sharedSettingKeysCache.set(registry, shared);
  return shared;
}

/**
 * Compute the full set of matrix row keys for a workflow's Actions tab.
 *
 * Rows are derived at runtime from the workflow's own declarations — the
 * workflow yaml and `smarttask-config.yaml` are the single source of truth.
 * There is no manifest file to keep in sync: if a tile exists in the
 * SmartTask UI, its key appears here automatically.
 *
 * Rows are the union of exactly the keys the UI gates on
 * `isItemTypeVisibleInSmartTask(..., workflowKey)`:
 *   1. `begin_action.workflow_sections[].actions[].key` — the begin-action
 *      buttons shown on the Smart Task sheet (gated by `<key>.smart_task`)
 *   2. SmartTask modal action keys from `smarttask-config.yaml` — the tiles
 *      inside the workflow modal (gated by `<key>.smart_task` in
 *      WorkflowModalBase)
 *
 * `allowed_item_types` and `applies_to_item_type` are deliberately NOT rows:
 * they are the data types a workflow *operates on* inside its views, not
 * toggles a user flips. Including them produced "garbage" rows (e.g. fleet
 * showed asset/maintenance/vehicle rows that gate nothing).
 *
 * `smart_task.quick_actions` and `requires_permission` coordinate prefixes
 * are likewise NOT rows: quick actions are feature-flag-gated buttons, and
 * coordinates may reference rows owned by other workflows (e.g.
 * `stock_adjustment.issue_to_job` lives in asset-inventory-management).
 */
export function getWorkflowMatrixItemTypes(
  workflowKey: string,
  registry: IndustrySolutionConfigRegistry,
): string[] {
  const template = registry.getWorkflowTemplate(workflowKey);
  const rows: string[] = [];
  const seen = new Set<string>();

  const push = (key: string | undefined | null) => {
    const normalized = typeof key === 'string' ? key.trim().toLowerCase().replace(/[\s-]+/g, '_') : '';
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      rows.push(normalized);
    }
  };

  if (template) {
    for (const action of getWorkflowSmartTaskActions(template)) {
      push(action.key);
    }
    // extra_matrix_rows: additional RBAC-only matrix rows declared on the
    // template's smart_task config (e.g. the expenses Smart Capture
    // linking-mode selector). They appear in the Actions tab matrix but do
    // not render as begin-action buttons or modal tiles.
    const smartTaskConfig = template.config?.smart_task as Record<string, unknown> | undefined;
    const extraRows = smartTaskConfig?.extra_matrix_rows;
    if (Array.isArray(extraRows)) {
      for (const row of extraRows) {
        if (typeof row === 'string') {
          push(row);
        } else if (row && typeof row === 'object') {
          push((row as Record<string, unknown>).key as string | undefined);
        }
      }
    }
  }
  for (const key of getWorkflowModalActionKeys(workflowKey, registry)) {
    push(key);
  }

  return rows;
}

/**
 * Get the Actions tab columns for a workflow. Derived from the template's
 * `smart_task.matrix_actions`; falls back to all OPERATIONS_ACTION_KEYS.
 */
export function getWorkflowMatrixColumns(
  workflowKey: string,
  registry: IndustrySolutionConfigRegistry,
): string[] {
  const template = registry.getWorkflowTemplate(workflowKey);
  const derived = template ? getWorkflowMatrixActions(template) : [];
  return derived.length > 0 ? derived : [...OPERATIONS_ACTION_KEYS];
}

/**
 * Feature/policy keys + seed defaults for a workflow group, derived from the
 * workflow's own declarations:
 *   - every modal `rbac_permission` seeds true (modal access)
 *   - `configurable_setting_keys` + non-shared `shared_setting_keys` seed to
 *     their value in permission-defaults.yaml (false when absent)
 *
 * Returns null when the workflow declares no features at all.
 */
export function getWorkflowFeatureDefaults(
  workflowKey: string,
  registry: IndustrySolutionConfigRegistry,
): Record<string, unknown> | null {
  const features: Record<string, unknown> = {};

  const template = registry.getWorkflowTemplate(workflowKey);
  const smartTask = template?.config?.smart_task as Record<string, unknown> | undefined;
  const sharedKeys = getSharedSmartTaskSettingKeys(registry);
  const configKeys = [
    ...(Array.isArray(smartTask?.configurable_setting_keys) ? smartTask.configurable_setting_keys : []),
    ...(Array.isArray(smartTask?.shared_setting_keys) ? smartTask.shared_setting_keys : []),
  ].filter((key): key is string => typeof key === 'string' && !sharedKeys.has(key));

  const policyDefaults = getSolutionPolicyDefaults();
  for (const key of configKeys) {
    features[key] = key in policyDefaults ? policyDefaults[key] : false;
  }

  return Object.keys(features).length > 0 ? features : null;
}

/**
 * Build the seeded `operations_action_permissions` JSONB for a workflow group
 * from the derived matrix rows (see getWorkflowMatrixItemTypes). Each row is
 * expanded to all OPERATIONS_ACTION_KEYS using the permission-defaults.yaml
 * item-type defaults; rows missing there get the default fill (smart_task +
 * resolve true, everything else false).
 *
 * Returns null when the workflow has no matrix rows so callers can skip
 * seeding the key entirely.
 */
export function getWorkflowMatrixSeed(
  workflowKey: string,
  registry: IndustrySolutionConfigRegistry,
): OperationsActionPermissions | null {
  const rows = getWorkflowMatrixItemTypes(workflowKey, registry);
  if (rows.length === 0) return null;

  const fullOap = getSolutionPolicyDefaults()['operations_action_permissions'];
  const split = splitActionPermissionsByWorkflow(fullOap, { _workflow: rows });
  return split['_workflow'] ?? { item_types: {} };
}

/**
 * Split a full `operations_action_permissions` JSONB object into per-workflow
 * subsets, keyed by workflow template key. Each subset contains only the item
 * types that belong to that workflow.
 *
 * Item types that don't belong to any enabled workflow are placed in the
 * 'general' bucket so they're not lost.
 *
 * @param fullPermissions - The complete operations_action_permissions object
 * @param workflowItemTypes - Map from workflow template key to its item types
 * @returns Map from workflow key to a partial OperationsActionPermissions
 */
export function splitActionPermissionsByWorkflow(
  fullPermissions: unknown,
  workflowItemTypes: Record<string, string[]>,
): Record<string, OperationsActionPermissions> {
  const normalized = normalizeOperationsActionPermissions(fullPermissions);
  const result: Record<string, OperationsActionPermissions> = {};

  // Build reverse map: itemType → workflow keys that include it
  const itemTypeToWorkflows: Record<string, string[]> = {};
  for (const [workflowKey, itemTypes] of Object.entries(workflowItemTypes)) {
    for (const itemType of itemTypes) {
      if (!itemTypeToWorkflows[itemType]) itemTypeToWorkflows[itemType] = [];
      itemTypeToWorkflows[itemType].push(workflowKey);
    }
  }

  for (const [itemType, actionMap] of Object.entries(normalized.item_types)) {
    const workflowKeys = itemTypeToWorkflows[itemType];
    if (workflowKeys && workflowKeys.length > 0) {
      // Assign to each workflow that uses this item type
      for (const wk of workflowKeys) {
        if (!result[wk]) result[wk] = { item_types: {} };
        result[wk].item_types[itemType] = actionMap;
      }
    } else {
      // Item type doesn't belong to any workflow — keep in 'general'
      if (!result['general']) result['general'] = { item_types: {} };
      result['general'].item_types[itemType] = actionMap;
    }
  }

  // Ensure all workflow item types have an entry, even if the source
  // (permission-defaults.yaml) doesn't include them. This is critical
  // because the YAML may not cover all 42 item types used by workflows.
  // Without this, item types like 'route', 'inventory', 'manifest', etc.
  // would be silently missing from child workflow group action permissions.
  // Include ALL OPERATIONS_ACTION_KEYS so that new action keys (smart_task,
  // view, etc.) are present in seeded policies from the start.
  // smart_task defaults to true so that seeded workflow rows are ticked by
  // default (admins can uncheck them in the Actions tab matrix).
  const DEFAULT_ACTION_MAP = OPERATIONS_ACTION_KEYS.reduce<OperationsActionMap>((acc, key) => {
    acc[key] = key === 'resolve' || key === 'smart_task';
    return acc;
  }, {});

  // Fill in missing action keys for ALL item types (existing from defaults
  // AND newly created). This ensures new action keys like 'smart_task' and
  // 'view' are present even when the YAML defaults predate them.
  for (const workflowResult of Object.values(result)) {
    for (const actionMap of Object.values(workflowResult.item_types)) {
      for (const actionKey of OPERATIONS_ACTION_KEYS) {
        if (actionMap[actionKey] === undefined) {
          actionMap[actionKey] = DEFAULT_ACTION_MAP[actionKey];
        }
      }
    }
  }

  for (const [workflowKey, itemTypes] of Object.entries(workflowItemTypes)) {
    if (!result[workflowKey]) result[workflowKey] = { item_types: {} };
    for (const itemType of itemTypes) {
      if (!result[workflowKey].item_types[itemType]) {
        result[workflowKey].item_types[itemType] = { ...DEFAULT_ACTION_MAP };
      }
    }
  }

  return result;
}

/**
 * Group all permission keys for a solution by their source workflow template.
 *
 * Returns a map from workflow template key (or 'general' / 'smarttask') to
 * the set of permission keys that belong to that workflow. This is used by
 * the solution group sync to create per-workflow child permission groups.
 *
 * Categories:
 *   - '<templateKey>' — permissions specific to one workflow template
 *     (includes `operations_action_permissions` — each workflow gets this
 *     key, but seeded with only its own item types)
 *   - 'general' — default solution policies plus SmartTask shell-level
 *     settings shared across workflows (smart_task_field_worker etc.) —
 *     administered once on the solution parent group
 *
 * @param solutionKey - The industry solution key
 * @param registry - The config registry
 * @param enabledTemplateKeys - Optional set of enabled template keys to filter by
 * @returns Map from workflow key to array of permission keys
 */
export function getPermissionsGroupedByWorkflow(
  solutionKey: string,
  registry: IndustrySolutionConfigRegistry,
  enabledTemplateKeys?: Set<string> | null,
): Record<string, string[]> {
  const keyMapping = getWorkflowKeyMappingForSolution(solutionKey, registry, enabledTemplateKeys);
  const allKeys = getSolutionPermissionKeys(solutionKey, registry, enabledTemplateKeys);

  const solution = registry.getSolution(solutionKey);

  const grouped: Record<string, string[]> = {};
  for (const permKey of allKeys) {

    // operations_action_permissions is assigned to EVERY enabled workflow
    // (each child group gets this key, but seeded with only its own item types)
    if (permKey === 'operations_action_permissions') {
      if (solution?.mappings?.workflow_templates) {
        for (const templateKey of expandWithSmartTaskCompanions(solution.mappings.workflow_templates)) {
          if (enabledTemplateKeys && !enabledTemplateKeys.has(templateKey)) continue;
          if (!grouped[templateKey]) grouped[templateKey] = [];
          grouped[templateKey].push(permKey);
        }
      }
      continue;
    }

    const workflowKey = keyMapping[permKey] || 'general';
    if (!grouped[workflowKey]) grouped[workflowKey] = [];
    grouped[workflowKey].push(permKey);
  }

  return grouped;
}
